import React from "react";
import { toast } from "react-toastify";
import { Button, Modal } from "@/Components/Common/Tailwind";
import { API, SITE_URL } from "../../api";
import GiftPhoneNumList from "../../pages/UserPage/purchaseList/GiftPhoneNumList.jsx";
import { getAccessToken } from "../../api/auth";
import { shareKakao } from "../../common/social/kakao/api";
import { handleCopyClipBoard } from "../../common/utils/clipboard";
import {
  buildShareUrl,
  getGiftSendErrorMessage,
  getRequiredResponseValue,
} from "../../common/social/kakao/giftShare.mjs";

interface Coupon {
  discount_type: string;
  discount_amount: number;
  discount_rate: number;
  expire_date: string | null;
  bound_type: string;
  use_start_date: string;
  use_end_date: string;
  min_purchase: number;
  image?: string;
}

interface CouponGiftInfo {
  id: number;
  coupon_name: string;
  state: string;
  coupon: Coupon;
  share_pin_code?: string;
  share_count?: number;
  share_state?: string;
}

interface CouponGiftModalProps {
  info: CouponGiftInfo;
  setInfo: (info: CouponGiftInfo | {}) => void;
  visible: boolean;
  setVisible: (visible: boolean) => void;
  needRefresh: () => void;
}

const CouponGiftModal = ({ info, setInfo, visible, setVisible, needRefresh }: CouponGiftModalProps) => {
  const togModal = () => setVisible(!visible);
  const [giftShareUrl, setGiftShareUrl] = React.useState("");
  const [isSharing, setIsSharing] = React.useState(false);

  React.useEffect(() => {
    if (!visible) {
      setGiftShareUrl("");
      setIsSharing(false);
    }
  }, [visible, (info as CouponGiftInfo)?.id]);

  const sendUserCoupon = async (couponId: number | string, phoneNum: string) => {
    const phone_num = phoneNum.replace(/-/g, "");

    const accessToken = getAccessToken();

    try {
      const response = await API.post(
        "/coupon/gift/send/",
        {
          user_coupon_id: couponId,
          phone_num: phone_num,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const data = await response.data;
      return data;
    } catch (error) {
      console.error(error);
      // Handle errors as needed
      throw error;
    }
  };
  const giftCouponByUrl = async (couponId: number) => {
    const accessToken = getAccessToken();

    try {
      const response = await API.post(
        `/coupon/gift/send/url/`,
        {
          user_coupon_id: couponId,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const data = response.data;
      return data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const handleGiftUrlShare = async () => {
    if (isSharing) return;

    setIsSharing(true);
    try {
      let shareUrl = giftShareUrl;

      if (!shareUrl) {
        const data = await giftCouponByUrl(info.id);
        const giftId = getRequiredResponseValue(data, "gift_id");
        shareUrl = buildShareUrl(SITE_URL, ["gift", "coupon", giftId]);
        setGiftShareUrl(shareUrl);
        needRefresh();
      }

      const opened = shareKakao({
        title: `${info.coupon_name}`,
        description: "쿠폰상품을 선물 받았습니다.",
        imageUrl: info.coupon?.image ? info.coupon.image : "",
        shareUrl,
      });

      if (opened) {
        toast.success("카카오톡 공유창을 열었습니다.");
        setInfo({});
        setVisible(false);
        return;
      }

      await handleCopyClipBoard(shareUrl, {
        successMessage: "카카오톡 공유를 열 수 없어 링크를 복사했어요.",
        errorMessage: "카카오톡 공유를 열 수 없습니다. 링크를 복사해 주세요.",
      });
    } catch (error) {
      console.error(error);
      toast.error(getGiftSendErrorMessage(error, "선물하기에 실패했습니다."));
    } finally {
      setIsSharing(false);
    }
  };

  if (!Object.keys(info).length) return null;

  return (
    <React.Fragment>
      <Modal toggle={togModal} isOpen={visible} centered>
        <div>
          <div className="mb-4 flex justify-center">
            <h4 className="text-lg font-semibold">쿠폰 양도</h4>
          </div>
          <ul className="list-group">
            <li className="list-group-item">쿠폰이름: {info?.coupon_name}</li>
            <li className="list-group-item">사용가능한 지점 : {info?.state}</li>
            <li className="list-group-item">
              할인금액 : {info?.coupon.discount_type === "price" ? `${info.coupon.discount_amount.toLocaleString()}원 할인쿠폰` : `${info.coupon.discount_rate}% 할인쿠폰`}
            </li>
            <li className="list-group-item">만료기한 : {info.coupon.expire_date}</li>
            {info.coupon.bound_type === "reservation" ? (
              <li className="list-group-item">
                예약가능기간: {info.coupon.use_start_date} ~ {info.coupon.use_end_date}
              </li>
            ) : null}

            <li className="list-group-item">사용정보 : {info.coupon.min_purchase.toLocaleString()}원 이상 구매시 사용가능</li>
            <li className="list-group-item">이용정보 : {info.state}</li>
          </ul>

          <div style={{ marginTop: "24px" }}></div>
          <div className="flex justify-center mx-1 mb-4">
            <Button
              variant="primary"
              className="gap-2 btn-kakao"
              disabled={isSharing}
              onClick={handleGiftUrlShare}
            >
              <i className="ri-gift-fill"></i>
              {isSharing ? "공유 준비중..." : "카카오톡으로 선물보내기"}
            </Button>
          </div>

          {giftShareUrl ? (
            <div className="mx-1 mb-4">
              <label className="mb-1 block text-sm text-gray-500" htmlFor="couponGiftShareUrl">
                공유 링크
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="couponGiftShareUrl"
                  readOnly
                  value={giftShareUrl}
                  className="h-9 min-w-0 flex-1 rounded border border-gray-300 px-2 text-sm"
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={isSharing}
                  onClick={() => handleCopyClipBoard(giftShareUrl)}
                >
                  복사
                </Button>
              </div>
            </div>
          ) : null}

          <div style={{ marginTop: "24px" }}></div>
          <GiftPhoneNumList sendItem={sendUserCoupon} {...{ info, setInfo, visible, setVisible, needRefresh }} />
        </div>
      </Modal>
    </React.Fragment>
  );
};
export default CouponGiftModal;
